// Copyright (c) 2022 by Move Closer

import { onMounted, PropType, ref, SetupContext, watch } from '@vue/composition-api'

import { ComponentObjectPropsOptions } from '../../vue-api'
import { cardCheckProps } from '../CardCheck/CardCheck.hooks'

import {
  AsyncCardCheckMiddleware,
  AsyncCardCheckProps,
  AsyncCardCheckUseProps,
  UseAsyncCardCheckProvides
} from './AsyncCardCheck.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 */
export const asyncCardCheckProps: ComponentObjectPropsOptions<AsyncCardCheckProps> = {
  ...cardCheckProps,

  middleware: {
    type: Function as PropType<AsyncCardCheckMiddleware>,
    required: false
  },

  rememberCandidate: {
    type: Boolean,
    required: false,
    default: false
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 */
function isCandidateValue (model: unknown | unknown[], value: unknown): boolean {
  return Array.isArray(model) ? model.includes(value) : model === value
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl> (original)
 */
export function useAsyncCardCheck (props: AsyncCardCheckUseProps, ctx: SetupContext): UseAsyncCardCheckProvides {
  const candidateValue = ref<unknown>(props.model)
  const isCandidate = ref<boolean>(false)

  function resolveCandidate (result: boolean): void {
    if (result) {
      ctx.emit('update:model', candidateValue.value)
    } else {
      candidateValue.value = props.model
    }

    isCandidate.value = false
  }

  function _considerRememberingCandidate (): void {
    isCandidate.value = !!props.rememberCandidate
  }

  async function onCandidateChange (value: unknown): Promise<void> {
    candidateValue.value = value
    isCandidate.value = isCandidateValue(value, props.value)

    if (isCandidate.value && typeof props.middleware === 'function') {
      const middlewareReturned = props.middleware()

      let result: boolean

      try {
        result = middlewareReturned instanceof Promise ? await middlewareReturned : middlewareReturned
      } catch (e) {
        result = false
      }

      resolveCandidate(result)
    }
  }

  onMounted(() => {
    isCandidate.value = props.model === props.value
  })

  watch(props, () => {
    _considerRememberingCandidate()
  })

  return { candidateValue, isCandidate, onCandidateChange, resolveCandidate }
}
